import { Link, data } from "react-router";
import { useTranslation } from "react-i18next";
import type { Route } from "./+types/$id";
import { Container } from "~/components/layout/Container";
import { MerchantForms } from "~/components/merchant/MerchantForms";
import { MerchantHeader } from "~/components/merchant/MerchantHeader";
import { PartySiteList } from "~/components/merchant/PartySiteList";
import {
  type ServiceOffered,
  ServicesOffered,
} from "~/components/merchant/ServicesOffered";
import {
  getAccessApplicationLinksByAAIds,
  getAccessApplicationsByMerchantId,
  getMerchantById,
  getMerchantItemsByItemIds,
} from "~/lib/api/merchants.server";
import {
  getServiceItemRelationships,
  getServiceItems,
} from "~/lib/api/services.server";
import {
  getLocationsByIds,
  getPartySitesByPartyId,
} from "~/lib/api/booking.server";
import { getLoginUrl } from "~/lib/auth/auth";
import {
  clearAuthCookieHeaders,
  clearSessionCookieHeaders,
  getValidAccessToken,
} from "~/lib/auth/verify-token.server";
import { merchantJsonLd } from "~/lib/seo/seo";
import { safe } from "~/lib/safe";
import { cacheHeaders, withCookieAwareCache } from "~/lib/cache-headers";
import { safeJsonLd } from "~/lib/seo/escape";
import type {
  AccessApplication,
  AccessApplicationLink,
  PartySiteWithLocation,
} from "~/lib/types";

const fetchPartySites = async (
  partyId: string | undefined,
  token: string,
): Promise<PartySiteWithLocation[]> => {
  if (!partyId) return [];
  try {
    const sites = await getPartySitesByPartyId(partyId, token);
    if (sites.length === 0) return [];
    const locationIds = [
      ...new Set(
        sites.map((s: (typeof sites)[0]) => s.location_id).filter(Boolean),
      ),
    ];
    const locations =
      locationIds.length > 0 ? await getLocationsByIds(locationIds, token) : [];
    const locationMap = new Map(
      locations.map((l: (typeof locations)[0]) => [l.id, l]),
    );
    return sites.map((site: (typeof sites)[0]) => ({
      ...site,
      location: locationMap.get(site.location_id) ?? null,
    }));
  } catch {
    return [];
  }
};

const groupLinksByAaId = (
  links: AccessApplicationLink[],
): Record<string, AccessApplicationLink[]> =>
  links.reduce<Record<string, AccessApplicationLink[]>>(
    (acc, link) => ({
      ...acc,
      [link.aa_id]: [...(acc[link.aa_id] ?? []), link],
    }),
    {},
  );

const buildServicesOffered = ({
  merchantId,
  items,
  relationships,
  merchantItems,
}: {
  merchantId: string;
  items: Awaited<ReturnType<typeof getServiceItems>>;
  relationships: Awaited<ReturnType<typeof getServiceItemRelationships>>;
  merchantItems: Awaited<ReturnType<typeof getMerchantItemsByItemIds>>;
}): ServiceOffered[] => {
  const myItemIds = new Set(
    merchantItems
      .filter(mi => mi.merchant_id === merchantId)
      .map(mi => mi.item_id),
  );
  const parentIdByItem = new Map(
    relationships.map(r => [r.item_id, r.related_item_id]),
  );
  const itemById = new Map(items.map(i => [i.id, i]));

  return items
    .filter(i => myItemIds.has(i.id))
    .map(item => {
      const parent = itemById.get(parentIdByItem.get(item.id) ?? "");
      return {
        name: item.item_name,
        description: item.item_description,
        categoryName: parent?.item_name ?? "",
        categoryKey: parent?.item_key ?? "",
      };
    })
    .filter(s => s.categoryKey);
};

export async function loader({ params, request }: Route.LoaderArgs) {
  const { id } = params;
  const merchant = await getMerchantById(id);
  if (!merchant) throw new Response("Not Found", { status: 404 });

  const { token, shouldClear, isExpired } = await getValidAccessToken(request);
  const [partySites, aas, items, relationships] = await Promise.all([
    token
      ? fetchPartySites(merchant.party_id as string | undefined, token)
      : Promise.resolve([] as PartySiteWithLocation[]),
    safe(
      () => getAccessApplicationsByMerchantId(id),
      [] as AccessApplication[],
    ),
    safe(
      () => getServiceItems(),
      [] as Awaited<ReturnType<typeof getServiceItems>>,
    ),
    safe(
      () => getServiceItemRelationships(),
      [] as Awaited<ReturnType<typeof getServiceItemRelationships>>,
    ),
  ]);

  const serviceIds = items
    .filter(i => i.item_type === "SERVICE")
    .map(i => i.id);
  const merchantItems = await safe(
    () => getMerchantItemsByItemIds(serviceIds),
    [] as Awaited<ReturnType<typeof getMerchantItemsByItemIds>>,
  );
  const servicesOffered = buildServicesOffered({
    merchantId: id,
    items,
    relationships,
    merchantItems,
  });

  const aaLinks = token
    ? await safe(
        () =>
          getAccessApplicationLinksByAAIds(
            aas.map(a => a.id),
            token,
          ),
        [] as AccessApplicationLink[],
      )
    : [];
  const linksByAaId = groupLinksByAaId(aaLinks);

  const result = {
    merchant,
    partySites,
    aas,
    linksByAaId,
    servicesOffered,
    isAuthed: !!token,
  };

  return shouldClear
    ? data(result, {
        headers: isExpired
          ? clearSessionCookieHeaders()
          : clearAuthCookieHeaders(),
      })
    : result;
}

export const headers: Route.HeadersFunction = ({ loaderHeaders }) =>
  withCookieAwareCache(loaderHeaders, cacheHeaders["public-300"]);

export const meta: Route.MetaFunction = ({ data: loaderData }) => {
  if (!loaderData) return [{ title: "Merchant Not Found" }];
  const { merchant } = loaderData;
  const name = String(merchant.merchant_name ?? "Unnamed Merchant");
  const description = String(
    merchant.merchant_description ??
      "View this merchant on Raytio Trust Marketplace.",
  );
  const logo = merchant.merchant_image_url_logo;
  return [
    { title: name },
    { name: "description", content: description },
    { property: "og:title", content: name },
    { property: "og:description", content: description },
    ...(logo ? [{ property: "og:image", content: String(logo) }] : []),
  ];
};

export default function MerchantDetailRoute({
  loaderData,
  params,
}: Route.ComponentProps) {
  const { merchant, partySites, aas, linksByAaId, servicesOffered, isAuthed } =
    loaderData;
  const { id } = params;
  const { t } = useTranslation();

  return (
    <section className="py-12">
      <Container>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd(merchantJsonLd(merchant)),
          }}
        />

        {/* Header card */}
        <div className="rounded-2xl border border-border-shell/60 dark:border-transparent bg-surface-blank p-8 shadow-simple">
          <MerchantHeader
            name={(merchant.merchant_name as string | null | undefined) ?? null}
            commonName={
              (merchant.merchant_common_name as string | null | undefined) ??
              null
            }
            description={
              (merchant.merchant_description as string | null | undefined) ??
              null
            }
            logoUrl={
              (merchant.merchant_image_url_logo as string | null | undefined) ??
              null
            }
            statusCode={
              (merchant.merchant_status_code as string | null | undefined) ??
              null
            }
            approvalStatus={
              (merchant.merchant_approval_status_code as
                | string
                | null
                | undefined) ?? null
            }
          />
        </div>

        {/* Price list link */}
        <div className="mt-4 flex justify-end">
          <Link
            to={`/merchants/${id}/price-list`}
            className="inline-flex items-center gap-1 rounded-lg border border-border-shell px-4 py-2 font-heading text-xs font-semibold uppercase tracking-wider text-text-medium transition hover:bg-surface-elevated hover:text-text-full"
          >
            {t("MerchantDetail.view-price-list")}
          </Link>
        </div>

        {/* Body */}
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="space-y-12">
            <ServicesOffered merchantId={id} services={servicesOffered} />
            <MerchantForms
              merchantId={id}
              aas={aas}
              linksByAaId={linksByAaId}
              isAuthed={isAuthed}
            />
          </div>

          <div>
            {isAuthed ? (
              <PartySiteList sites={partySites} />
            ) : (
              <div className="rounded-xl border border-border-shell p-6 text-center">
                <h2 className="font-heading text-lg font-semibold text-text-full">
                  {t("MerchantDetail.sign-in-to-view")}
                </h2>
                <p className="mt-2 text-sm text-text-medium">
                  {t("MerchantDetail.sign-in-prompt")}
                </p>
                <a
                  href={getLoginUrl(`/merchants/${id}`)}
                  className="mt-4 inline-block rounded-lg bg-accent px-6 py-2 font-heading text-sm font-semibold uppercase tracking-wider text-ink hover:brightness-95"
                >
                  {t("MerchantDetail.sign-in-to-view")}
                </a>
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
